import { useState } from "react";
import { InfinityMark } from "../components/ConnectMyHubLogo";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

export default function PortalSignup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({first_name:"", last_name:"", email:"", password:"", country:"", primary_role:""});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/candidate-portal/signup", f);
      setUser({id: data.user_id, email: data.email, name: `${f.first_name} ${f.last_name}`, role: "candidate"});
      nav("/portal");
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between p-12 flex-1 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center"><InfinityMark size={30}/></div>
          <div>
            <div className="font-display font-bold text-white text-lg">Connect<span className="text-teal-300">My</span>Hub<span className="text-teal-300 text-xs font-semibold ml-0.5">™</span></div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">Talent Network</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="text-[11px] text-sky-400 font-semibold tracking-widest uppercase mb-3">Join the network</div>
          <h1 className="text-white font-display font-bold text-4xl leading-tight tracking-tight mb-4">
            Free to join.<br/>Curated for your career.
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed">
            Candidates never pay ConnectMyHub. Build your profile, complete a video introduction, and let us match you to opportunities that fit.
          </p>
          <ul className="mt-6 text-xs text-slate-400 space-y-2">
            <li>· Free forever · no application fees, ever</li>
            <li>· Real people review every profile</li>
            <li>· Get matched to opportunities that fit your goals</li>
          </ul>
        </div>
        <div className="relative z-10 text-[11px] text-slate-500">© 2026 My Pipeline Solutions. All rights reserved.</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form data-testid="portal-signup-form" onSubmit={submit} className="w-full max-w-sm">
          <h2 className="font-display font-bold text-2xl text-slate-900 tracking-tight">Create your talent profile</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Applying to ConnectMyHub is always free.</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input required data-testid="signup-first" placeholder="First name" value={f.first_name} onChange={e=>setF({...f,first_name:e.target.value})} className="h-10 px-3 rounded-md border border-slate-300 text-sm outline-none focus:border-sky-500"/>
            <input required data-testid="signup-last" placeholder="Last name" value={f.last_name} onChange={e=>setF({...f,last_name:e.target.value})} className="h-10 px-3 rounded-md border border-slate-300 text-sm outline-none focus:border-sky-500"/>
          </div>
          <input required type="email" data-testid="signup-email" placeholder="Email" value={f.email} onChange={e=>setF({...f,email:e.target.value})} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm mb-3 outline-none focus:border-sky-500"/>
          <input required type="password" data-testid="signup-password" placeholder="Password (10+ chars, letters & digit)" value={f.password} onChange={e=>setF({...f,password:e.target.value})} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm mb-3 outline-none focus:border-sky-500"/>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <input placeholder="Country" data-testid="signup-country" value={f.country} onChange={e=>setF({...f,country:e.target.value})} className="h-10 px-3 rounded-md border border-slate-300 text-sm outline-none focus:border-sky-500"/>
            <input placeholder="Primary role" data-testid="signup-role" value={f.primary_role} onChange={e=>setF({...f,primary_role:e.target.value})} className="h-10 px-3 rounded-md border border-slate-300 text-sm outline-none focus:border-sky-500"/>
          </div>
          {err && <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
          <button data-testid="signup-submit" disabled={busy} className="w-full h-10 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin"/>} Create account
          </button>
          <div className="mt-4 text-xs text-slate-500 text-center">
            Already have an account? <Link to="/login" className="text-sky-700 font-semibold">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
